import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import SuperAdminLayout from '../../components/SuperAdmin/Layout/SuperAdminLayout';
import tenantService from '../../services/tenantService';
import moduleSubscriptionService from '../../services/moduleSubscriptionService';

import BranchHeader from './BranchHeader';
import BranchDetailsTab from './BranchDetailsTab';
import UsersTab from './UsersTab';
import SubscriptionTab from './SubscriptionTab';

export default function BranchDetails() {
    const { branchId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('details');
    const [branch, setBranch] = useState(location.state?.branch || null);
    const [organization, setOrganization] = useState(location.state?.organization || null);
    const [loading, setLoading] = useState(false);
    const [migrating, setMigrating] = useState(false);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [togglingUserId, setTogglingUserId] = useState(null);
    const [apiModules, setApiModules] = useState([]);
    const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);

    useEffect(() => {
        if (branchId) fetchBranchModules();
    }, [branchId]);

    useEffect(() => {
        fetchUsers();
    }, [branchId]);

    const fetchBranchModules = async () => {
        setSubscriptionsLoading(true);
        try {
            const response = await moduleSubscriptionService.getBranchModules(branchId);
            if (Array.isArray(response)) {
                setApiModules(response);
            } else {
                setApiModules([]);
            }
        } catch (error) {
            console.error('❌ Error fetching branch modules:', error);
            toast.error("Failed to fetch module subscriptions");
            setApiModules([]);
        } finally {
            setSubscriptionsLoading(false);
        }
    };

    const fetchUsers = async () => {
        setUsersLoading(true);
        try {
            const response = await tenantService.superAdminGetUsers(0, 100, '');
            const data = response.data || response;
            const branchUsers = (data.content || []).filter(user => user.branchId === branchId);
            setUsers(branchUsers);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error("Failed to fetch users");
        } finally {
            setUsersLoading(false);
        }
    };

    const handleRunMigration = async () => {
        if (!organization?.id || !branch?.id) return;
        setMigrating(true);
        try {
            await tenantService.superAdminRunTenantMigration(organization.id, branch.id);
            toast.success("Migration completed successfully");
        } catch (error) {
            console.error('Migration error:', error);
            toast.error("Migration failed");
        } finally {
            setMigrating(false);
        }
    };

    const handleToggleModule = async (moduleCode) => {
        const module = apiModules.find(m => m.code === moduleCode);
        if (!module) return;

        const isCurrentlySubscribed = module.subscription;
        const newSubscribedCodes = isCurrentlySubscribed
            ? apiModules.filter(m => m.subscription && m.code !== moduleCode).map(m => m.code)
            : [...apiModules.filter(m => m.subscription).map(m => m.code), moduleCode];

        try {
            await moduleSubscriptionService.updateBranchModules(branchId, newSubscribedCodes);
            toast.success(isCurrentlySubscribed ? "Module unsubscribed successfully" : "Module subscribed successfully");
            await fetchBranchModules();
        } catch (error) {
            console.error('❌ Error toggling module:', error);
            toast.error("Failed to update subscription");
        }
    };

    const handleToggleBranchStatus = async () => {
        if (!branch?.id) return;
        const newStatus = !branch.active;
        try {
            await tenantService.branchStatus(branch.id, newStatus);
            toast.success(`Branch ${newStatus ? 'activated' : 'deactivated'} successfully`);
            setBranch(prev => ({ ...prev, active: newStatus }));
        } catch (error) {
            console.error('Error updating branch status:', error);
            toast.error("Failed to update branch status");
        }
    };

    const handleToggleUserStatus = async (user) => {
        setTogglingUserId(user.id);
        const newStatus = !user.active;
        try {
            await tenantService.superAdminUpdateUserStatus(user.id, newStatus);
            toast.success(`User ${newStatus ? 'activated' : 'deactivated'}`);
            setUsers(prev => prev.map(u => u.id === user.id ? { ...u, active: newStatus } : u));
        } catch (error) {
            console.error('Error updating user status:', error);
            toast.error("Failed to update user status");
        } finally {
            setTogglingUserId(null);
        }
    };

    const subscribedModuleCodes = apiModules.filter(m => m.subscription).map(m => m.code);

    const sortedApiModules = [...apiModules].sort((a, b) => {
        if (a.subscription === b.subscription) return 0;
        return a.subscription ? -1 : 1;
    });

    if (!branch && loading) {
        return (
            <SuperAdminLayout>
                <div className="flex items-center justify-center h-full">
                    <Loader2 className="animate-spin text-indigo-600" size={32} />
                </div>
            </SuperAdminLayout>
        );
    }

    return (
        <SuperAdminLayout>
            <div className="flex flex-col h-full bg-slate-50">
                <BranchHeader
                    branch={branch}
                    organization={organization}
                    users={users}
                    subscribedModuleCodes={subscribedModuleCodes}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    handleToggleBranchStatus={handleToggleBranchStatus}
                />

                <div className="flex-1 overflow-y-auto p-8">
                    {activeTab === 'details' && (
                        <BranchDetailsTab
                            branch={branch}
                            organization={organization}
                            users={users}
                            migrating={migrating}
                            handleRunMigration={handleRunMigration}
                            handleToggleBranchStatus={handleToggleBranchStatus}
                        />
                    )}
                    {activeTab === 'users' && (
                        <UsersTab
                            users={users}
                            usersLoading={usersLoading}
                            togglingUserId={togglingUserId}
                            handleToggleUserStatus={handleToggleUserStatus}
                        />
                    )}
                    {activeTab === 'subscription' && (
                        <SubscriptionTab
                            apiModules={sortedApiModules}
                            handleToggleModule={handleToggleModule}
                            loading={subscriptionsLoading}
                        />
                    )}
                </div>
            </div>
        </SuperAdminLayout>
    );
}